import React, {useContext, useEffect, useLayoutEffect} from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  ScrollRestoration,
  useLocation,
  useLoaderData,
} from "react-router-dom";
import {Grid, useMediaQuery, useTheme} from "@mui/material";

import "./App.css";

// Pages
import Homepage, {DataLoader} from "./pages/app/home";
import CollectionsPage from "./pages/app/collections";
import PropertiesPage from "./pages/app/properties";
import LoginPage from "./pages/authentication/login";
import SignUpPage from "./pages/authentication/signup";
import ProductsListAdmin from "./pages/admin/products";
import Orders from "./pages/admin/orders";
import Categories from "./pages/admin/categories";
import AllUsers from "./pages/admin/users";
import CartPage from "./pages/app/cart";
import AdminDashboard from "./pages/admin/dashboard";
import LikedItemsPage from "./pages/app/likedItems";
import UserProfilePage from "./pages/user/UserProfilePage";
import UserAddressesPage from "./pages/user/UserAddressesPage";
import UserOrdersPage from "./pages/user/UserOrdersPage";
import OrderDetailPage from "./pages/user/OrderDetailPage";
import PageNotFound from "./pages/errors/PageNotFound";
import ContactUsPage from "./pages/app/contactUs";
import TermsAndConditions from "./pages/app/termsAndConditions";
import PrivacyPolicy from "./pages/app/privacy";
import CouponsListAdmin from "./pages/admin/coupons";
import SlidersListAdmin from "./pages/admin/sliders";
import PropertiesListAdmin from "./pages/admin/properties";
import StoreSettings from "./pages/admin/settings";

// Layouts
import {default as AdminDrawer} from "./components/layouts/admin/drawer";
import DrawerAppBar from "./components/layouts/app/appbar/DrawerAppBar";

// Contexts
import CartItemsState from "./context/cartItems/CartItemsState";
import CartItemsContext from "./context/cartItems/CartItemsContext";
import UserProfileState from "./context/userProfile/UserProfileState";
import ColorModeState from "./context/colorMode/ColorModeState";
import AddProductState from "./context/addProduct/AddProductState";
import AllProductState from "./context/allProducts/AllProductsState";
import AllCategoriesState from "./context/allCategories/AllCategoriesState";
import AllCategoriesContext from "./context/allCategories/AllCategoriesContext";
import AllProductContext from "./context/allProducts/AllProductsContext";
import AddCategoryState from "./context/addCategory/AddCategoryState";
import RecentlyViewedState from "./context/recentlyViewed/RecentlyViewedState";
import LikedItemsState from "./context/likedItems/LikedItemsState";
import AllReviewsState from "./context/allReviews/AllReviewsState";
import AllCouponsState from "./context/allCoupons/AllCouponsState";
import AddCouponState from "./context/addCoupon/AddCouponState";
import AllSlidersState from "./context/allSliders/AllSlidersState";
import AddSliderState from "./context/addSlider/AddSliderState";
import AllPropertiesState from "./context/allProperties/AllPropertiesState";
import AllPropertiesContext from "./context/allProperties/AllPropertiesContext";
import AllSlidersContext from "./context/allSliders/AllSlidersContext";
import AddPropertyState from "./context/addProperty/AddPropertyState";

//Components
import PrivateRoute from "./components/private-route/PrivateRoute";
import NetworkStatus from "./components/network-status/NetworkStatus";
import RefundPolicy from "./pages/app/refundPolicy";
import AppFooter from "./components/layouts/app/footer/AppFooter";
import AboutPage from "./pages/app/about";
import ShippingPolicy from "./pages/app/shippingPolicy";
import FixedBottomNavigation from "./components/layouts/app/appbar/FixedBottomNavigation";
import ThankYouForOrder from "./components/app/PlaceOrder/ThankYouForOrder";

import metaPixel from "./utils/analytics/metaPixel";

const storefrontShouldRevalidate = () => false;

export default function App() {
  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      document.documentElement.classList.add("is-scrolling");
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        document.documentElement.classList.remove("is-scrolling");
      }, 900);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route
          path="/"
          id="storefront"
          element={<Root />}
          loader={DataLoader}
          shouldRevalidate={storefrontShouldRevalidate}
          errorElement={<PageNotFound />}
        >
          <Route index element={<Homepage />} />
          <Route path="collections" element={<CollectionsPage />}>
            <Route path=":categoryId" element={<CollectionsPage />}>
              <Route path=":itemId" element={<CollectionsPage />} />
            </Route>
          </Route>
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/:propertyId" element={<PropertiesPage />} />
          <Route path="/contactUs" element={<ContactUsPage />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/about-inproveda" element={<AboutPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/myWishlist" element={<LikedItemsPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route
            path="/userProfile"
            element={<PrivateRoute Component={UserProfilePage} />}
          />
          <Route
            path="/userAddresses"
            element={
              <PrivateRoute accessTo="User" Component={UserAddressesPage} />
            }
          />
          <Route
            path="/userOrders"
            element={
              <PrivateRoute accessTo="User" Component={UserOrdersPage} />
            }
          />
          <Route
            path="/orderPlaced"
            element={
              <PrivateRoute accessTo="User" Component={ThankYouForOrder} />
            }
          />
          <Route
            path="/orderDetail"
            element={
              <PrivateRoute accessTo="User" Component={OrderDetailPage} />
            }
          />
        </Route>
        <Route
          path="/admin"
          element={
            <PrivateRoute accessTo="Admin" Component={Root} layout="ADMIN" />
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />}></Route>
          <Route path="/admin/products" element={<ProductsListAdmin />} />
          <Route path="/admin/categories" element={<Categories />} />
          <Route path="/admin/orders" element={<Orders />} />
          <Route path="/admin/users" element={<AllUsers />} />
          <Route path="/admin/coupons" element={<CouponsListAdmin />} />
          <Route path="/admin/sliders" element={<SlidersListAdmin />} />
          <Route path="/admin/properties" element={<PropertiesListAdmin />} />
          <Route path="/admin/settings" element={<StoreSettings />} />
        </Route>
      </>
    )
  );
  useEffect(() => {
    const trackLocation = (state) => {
      if (state.initialized && state.navigation.state === "idle") {
        metaPixel.pageView(state.location.pathname + state.location.search);
      }
    };
    trackLocation(router.state);
    return router.subscribe(trackLocation);
  }, [router]);

  return (
    <>
      <ColorModeState>
        <AllReviewsState>
          <AllProductState>
            <AllCategoriesState>
              <AddProductState>
                <AddCategoryState>
                  <UserProfileState>
                    <CartItemsState>
                      <LikedItemsState>
                        <RecentlyViewedState>
                          <AllCouponsState>
                            <AddCouponState>
                              <AllSlidersState>
                                <AddSliderState>
                                  <AllPropertiesState>
                                    <AddPropertyState>
                                      <RouterProvider router={router} />
                                    </AddPropertyState>
                                  </AllPropertiesState>
                                </AddSliderState>
                              </AllSlidersState>
                            </AddCouponState>
                          </AllCouponsState>
                        </RecentlyViewedState>
                      </LikedItemsState>
                    </CartItemsState>
                  </UserProfileState>
                </AddCategoryState>
              </AddProductState>
            </AllCategoriesState>
          </AllProductState>
        </AllReviewsState>
      </ColorModeState>
    </>
  );
}

const Root = ({layout = ""}) => {
  const theme = useTheme();
  const mobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const cartItemsContext = useContext(CartItemsContext);
  const allProductsContext = useContext(AllProductContext);
  const allCategoriesContext = useContext(AllCategoriesContext);
  const allPropertiesContext = useContext(AllPropertiesContext);
  const allSlidersContext = useContext(AllSlidersContext);
  const loaderData = useLoaderData();
  const storefront = loaderData?.storefront;

  useLayoutEffect(() => {
    if (!storefront) return;
    allProductsContext.setState(storefront.products);
    allCategoriesContext.setState(storefront.categories);
    allPropertiesContext.setState(storefront.properties);
    allSlidersContext.setState(storefront.sliders);
  }, [
    storefront,
    allProductsContext,
    allCategoriesContext,
    allPropertiesContext,
    allSlidersContext,
  ]);

  useEffect(() => {
    const loader = document.getElementById("loader");
    const root = document.getElementById("root");
    if (!loader) return undefined;

    const frame = window.requestAnimationFrame(() => {
      loader.classList.add("fadeOutLoader");
    });
    const hideTimer = window.setTimeout(() => {
      loader.style.display = "none";
      if (root) root.style.top = 0;
    }, 500);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(hideTimer);
    };
  }, []);

  // Hide the bottom nav on /cart only when the cart has items.
  // When the cart is empty, keep it visible so the user can navigate elsewhere.
  const cartHasItems =
    Array.isArray(cartItemsContext?.state) && cartItemsContext.state.length > 0;
  const hideBottomNav = location.pathname === "/cart" && cartHasItems;

  if (layout === "ADMIN") {
    return (
      <Grid container>
        <Grid item xs={12}>
          <NetworkStatus />
          <AdminDrawer />
        </Grid>
      </Grid>
    );
  } else if (layout === "") {
    return (
      <>
        <NetworkStatus />
        <DrawerAppBar
          categories={storefront?.categories}
          products={storefront?.products}
        />
        <Outlet />
        <AppFooter />
        {mobileScreen && !hideBottomNav && <FixedBottomNavigation />}
        <ScrollRestoration />
      </>
    );
  }
};
